import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Loader2, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [invitation, setInvitation] = useState<{
    email: string;
    name: string;
    role: string;
    expires_at: string;
    company_id: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const verifyInvitation = async () => {
      if (!token) {
        setError("Invalid invitation link");
        setIsLoading(false);
        return;
      }

      try {
        // Verify invitation token
        const { data, error: inviteError } = await (supabase as any)
          .from("user_invitations")
          .select("*")
          .eq("token", token)
          .is("accepted_at", null)
          .single();

        if (inviteError || !data) {
          setError("Invitation not found or already used");
          setIsLoading(false);
          return;
        }

        // Check if expired
        if (new Date(data.expires_at) < new Date()) {
          setError("Invitation has expired");
          setIsLoading(false);
          return;
        }

        setInvitation({
          email: data.email,
          name: data.name,
          role: data.role,
          expires_at: data.expires_at,
          company_id: data.company_id ?? null,
        });
      } catch (err) {
        setError("Failed to verify invitation");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    verifyInvitation();
  }, [token]);

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || password.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (!invitation) {
      toast({ title: "Error", description: "Invitation not found", variant: "destructive" });
      return;
    }

    if (!token) return;

    setIsAccepting(true);
    try {
      // Sign up the user
      const { error: signUpError } = await supabase.auth.signUp({
        email: invitation.email,
        password,
        options: {
          data: {
            display_name: invitation.name,
            role: invitation.role,
            company_id: invitation.company_id ?? null,
          },
        },
      });

      if (signUpError) throw signUpError;

      // Mark invitation as accepted
      const { error: updateError } = await (supabase as any)
        .from("user_invitations")
        .update({ accepted_at: new Date().toISOString() })
        .eq("token", token);

      if (updateError) {
        console.error("Error updating invitation:", updateError);
        // Don't fail if this errors, user is already created
      }

      toast({
        title: "Account created",
        description:
          "Your account has been created. Please check your email to verify your account.",
      });

      // Redirect to login
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create account";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8">
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
            <p className="text-sm text-muted-foreground">Verifying invitation...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="text-2xl font-display font-bold text-foreground mb-2">
              Invalid Invitation
            </h1>
            <p className="text-muted-foreground mb-6">{error || "Invitation not found"}</p>
            <Button onClick={() => navigate("/login")} variant="outline">
              Go to Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-xl gradient-primary flex items-center justify-center shadow-primary">
            <Zap className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-display font-bold text-foreground">Accept Invitation</h1>
          <p className="text-muted-foreground mt-2">
            You've been invited to join <strong>{invitation.name}</strong> as{" "}
            <strong>{invitation.role}</strong>
          </p>
        </div>

        <form onSubmit={handleAccept} className="space-y-4">
          <div className="p-4 bg-muted rounded-lg">
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email:</span>
                <span className="font-medium">{invitation.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Role:</span>
                <span className="font-medium">{invitation.role}</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Create Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />
          </div>

          <Button type="submit" className="w-full" disabled={isAccepting}>
            {isAccepting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating account...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Accept Invitation & Create Account
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Button variant="ghost" size="sm" onClick={() => navigate("/login")}>
            Already have an account? Sign in
          </Button>
        </div>
      </Card>
    </div>
  );
}
